import { Checkbox, Divider, Stack } from '@mui/material'
import { QcStudyDefinitionCreateFormValues } from '../../schemas/StudyAndSeriesDefinitionValidationSchema'
import { Control, useController } from 'react-hook-form'
import { QcTimepointContainerSubmission } from '@common/config/api/client'
import { useEffect } from 'react'
import { SPACING } from '@common/theme/spacing'
import { SelectField } from '../qc/SelectField'
import { InputField } from '../qc/InputField'
import SeriesDefinitionPopupMenuList from './SeriesDefinitionPopupMenuList'

interface Option {
  value: string
  label: string
  additional_fields?: {
    series_label_index?: number
    allow_qc_to_duplicate?: boolean
  }
}

// Function to format array of range objects to a readable string
function formatRangesForDisplay(ranges: Array<{ start: number; end: number }>): string {
  return ranges
    .map((range) => {
      // For single numbers
      if (range.start === range.end - 1) {
        return `${range.start}`
      }
      // For ranges
      return `${range.start}-${range.end - 1}`
    })
    .join(',')
}

export default function SeriesDefinitionForm({
  control,
  index,
  qcTimepointContainerSubmissions,
  defaultNewSeriesDescriptionValue,
  defaultSourceSeriesDescriptionValue,
  availableNewSeriesDescriptionOptions,
  availableSourceSeriesDescriptionOptions,
  handleDelete,
  handleDuplicate,
  handleMerge,
  allSeriesDescriptions,
  disableMerge,
  getNextAvailableIndex,
}: {
  control: Control<QcStudyDefinitionCreateFormValues>
  index: number
  qcTimepointContainerSubmissions: QcTimepointContainerSubmission[]
  defaultNewSeriesDescriptionValue: string
  defaultSourceSeriesDescriptionValue: string
  availableNewSeriesDescriptionOptions: Option[]
  availableSourceSeriesDescriptionOptions: Option[]
  handleDelete: () => void
  handleDuplicate: () => void
  handleMerge: (new_series_description: string, index?: number) => void
  allSeriesDescriptions: Option[]
  disableMerge?: boolean
  getNextAvailableIndex: (seriesLabelId: string) => number
}) {
  const { field: selectedField } = useController({
    name: `series_definitions.${index}.is_selected`,
    control,
  })

  const { field: maxInstanceField } = useController({
    name: `series_definitions.${index}.max_instance_number`,
    control,
  })

  const { field: instanceNumberRangesField } = useController({
    name: `series_definitions.${index}.instance_number_ranges`,
    control,
  })

  const { field: seriesLabelIndexField } = useController({
    name: `series_definitions.${index}.series_label_index`,
    control,
  })

  const { field: seriesLabelIdField } = useController({
    name: `series_definitions.${index}.series_label_id`,
    control,
  })

  const instanceNumber = qcTimepointContainerSubmissions
    .flatMap((submission) => submission.exam_metadata)
    .flatMap((metadata) => metadata.series)
    .find((series) => series.series_metadata_id === defaultSourceSeriesDescriptionValue)?.total_instances

  useEffect(() => {
    if (instanceNumber) {
      maxInstanceField.onChange(instanceNumber)

      // If instanceNumberRangesField has a value and it's an array of ranges
      if (
        instanceNumberRangesField.value &&
        Array.isArray(instanceNumberRangesField.value) &&
        instanceNumberRangesField.value.length > 0
      ) {
        // Format the existing ranges to string format
        instanceNumberRangesField.onChange(formatRangesForDisplay(instanceNumberRangesField.value))
      } else if (instanceNumberRangesField.value.length > 0) {
        instanceNumberRangesField.onChange(instanceNumberRangesField.value)
      } else {
        instanceNumberRangesField.onChange('1-' + instanceNumber)
      }
    }
  }, [instanceNumber])

  // Custom handler for series label selection to set the index
  const handleSeriesLabelSelection = (value: string) => {
    // When a user selects a new series label
    seriesLabelIdField.onChange(value)

    // Find the selected option to see if it has a pre-determined index
    const selectedOption = availableNewSeriesDescriptionOptions.find((option) => option.value === value)

    if (value) {
      // If the option has an index in additional_fields, use that
      if (selectedOption?.additional_fields?.series_label_index !== undefined) {
        seriesLabelIndexField.onChange(selectedOption.additional_fields.series_label_index)
      } else if (selectedOption?.additional_fields?.allow_qc_to_duplicate) {
        // If it's duplicatable but doesn't have an index yet, generate one
        seriesLabelIndexField.onChange(getNextAvailableIndex(value))
      } else {
        // For non-duplicatable items, use null
        seriesLabelIndexField.onChange(null)
      }
    } else {
      // Clear the index if no label is selected
      seriesLabelIndexField.onChange(null)
    }
  }

  return (
    <Stack
      direction="row"
      alignItems="start"
      justifyContent="space-between"
      spacing={SPACING.spacingLg}
      paddingX={SPACING.seriesDefinitionsFormSpacing}
    >
      <Stack direction="row" alignItems="start" width="100%" spacing={SPACING.minSpacing}>
        <Stack paddingTop={SPACING.seriesDefinitionsFormSpacing}>
          <Checkbox
            sx={{
              width: 32,
              height: 32,
            }}
            checked={!!selectedField.value}
            onChange={(e) => selectedField.onChange(e.target.checked)}
          />
        </Stack>
        <Stack direction="row" alignItems="start" width="100%" spacing={SPACING.minSpacing}>
          <SelectField
            control={control}
            name={`series_definitions.${index}.series_metadata_id`}
            label="Source series description"
            options={availableSourceSeriesDescriptionOptions}
            defaultValue={defaultSourceSeriesDescriptionValue}
          />
          <SelectField
            control={control}
            name={`series_definitions.${index}.series_label_id`}
            label="New series description"
            options={availableNewSeriesDescriptionOptions}
            defaultValue={defaultNewSeriesDescriptionValue}
            onChangeOverride={handleSeriesLabelSelection}
          />
          <InputField
            control={control}
            name={`series_definitions.${index}.instance_number_ranges`}
            label="Image №"
            sx={{
              width: '50%',
            }}
          />
        </Stack>
      </Stack>
      <Stack
        direction="row"
        spacing={SPACING.minSpacing}
        alignItems="center"
        paddingTop={SPACING.seriesDefinitionsPopupListSpacing}
      >
        <Divider orientation="vertical" flexItem />
        <SeriesDefinitionPopupMenuList
          control={control}
          index={index}
          newSeriesDescriptions={allSeriesDescriptions}
          handleDelete={handleDelete}
          handleDuplicate={handleDuplicate}
          handleMerge={handleMerge}
          disableMerge={disableMerge}
        />
      </Stack>
    </Stack>
  )
}
